import type { ReactNode } from "react";

export interface ComingSoonOverlayProps {
    /** Content rendered underneath the overlay (blurred and non-interactive while active). */
    children: ReactNode;
    /** When false, the overlay is hidden and children render normally. Defaults to true. */
    isActive?: boolean;
    /** Heading shown in the overlay card. */
    title?: string;
    /** Supporting text shown under the heading. */
    description?: string;
}

function ClockIcon() {
    return (
        <svg
            className="h-6 w-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
        </svg>
    );
}

/**
 * Wraps a not-yet-available section with a semi-transparent "coming soon"
 * overlay. The underlying content stays visible (blurred and inert) so the
 * layout is preserved, while all interaction is blocked.
 */
export function ComingSoonOverlay({
    children,
    isActive = true,
    title = "Coming soon",
    description = "This section isn't available yet. We're putting the finishing touches on it.",
}: ComingSoonOverlayProps) {
    if (!isActive) {
        return <>{children}</>;
    }

    return (
        <div className="relative">
            {children}

            {/* Semi-transparent frosted layer sits on top and captures all pointer
                events, so the underlying (mock) content is visible but inert. */}
            <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/50 p-4 backdrop-blur-sm" role="status" aria-live="polite">
                <div className="mt-24 flex max-w-sm flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white/95 px-6 py-5 text-center shadow-lg">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                        <ClockIcon />
                    </span>
                    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                    <p className="text-sm text-gray-600">{description}</p>
                </div>
            </div>
        </div>
    );
}
